/** Sections that open one at a time, or several, from a real disclosure button. */

import { useId, useState, type CSSProperties, type ReactNode } from "react";

import { cn } from "@/lib/cn";

import "./Accordion.css";

export interface AccordionItem {
  readonly id: string;
  readonly title: ReactNode;
  /** A value that stays visible while the section is shut. */
  readonly meta?: ReactNode;
  readonly content?: ReactNode;
  readonly disabled?: boolean;
}

export interface AccordionProps {
  readonly items: readonly AccordionItem[];
  readonly defaultOpenId?: string;
  /** Allow more than one open section. */
  readonly multiple?: boolean;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function Accordion({
  items,
  defaultOpenId,
  multiple = false,
  className,
  style,
}: AccordionProps) {
  const base = useId();
  const [open, setOpen] = useState<readonly string[]>(
    defaultOpenId === undefined ? [] : [defaultOpenId],
  );

  const toggle = (id: string) => {
    setOpen((held) => {
      if (held.includes(id)) return held.filter((each) => each !== id);
      return multiple ? [...held, id] : [id];
    });
  };

  return (
    <div className={cn("al-accordion", className)} style={style}>
      {items.map((item) => {
        const shown = open.includes(item.id);
        return (
          <div className="al-accordion-item" key={item.id}>
            <h3 className="al-accordion-heading">
              <button
                type="button"
                id={`${base}-head-${item.id}`}
                className="al-accordion-trigger"
                aria-expanded={shown}
                aria-controls={`${base}-body-${item.id}`}
                disabled={item.disabled === true}
                onClick={() => toggle(item.id)}
              >
                <span className="al-accordion-mark" aria-hidden="true">
                  {shown ? "-" : "+"}
                </span>
                <span className="al-accordion-title">{item.title}</span>
                {item.meta !== undefined ? (
                  <span className="al-accordion-meta">{item.meta}</span>
                ) : null}
              </button>
            </h3>
            <div
              id={`${base}-body-${item.id}`}
              role="region"
              aria-labelledby={`${base}-head-${item.id}`}
              className="al-accordion-body"
              hidden={!shown}
            >
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
}
